import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import { ToastContainer, toast } from "react-toastify";
import {
  Trash2,
  Pencil,
  BarChart3,
  BookOpen,
  Tags,
  Plus,
  Check,
  X,
  ChevronDown,
} from "lucide-react";
import {
  getFormationsAdmin,
  deleteFormation,
  publishFormation,
  unpublishFormation,
} from "../actions/formations.actions";
import {
  getFormationCategories,
  createFormationCategory,
  editFormationCategory,
  deleteFormationCategory,
} from "../actions/formationCategory.actions";
import { groupFormationsByCategory } from "../utils/formationGroups";

const AdminFormations = () => {
  const userData = useSelector((state) => state.userReducer);
  const { list } = useSelector((state) => state.formationsReducer);
  const categories = useSelector((state) => state.formationCategoryReducer);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [editCatId, setEditCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [collapsedCats, setCollapsedCats] = useState({});

  const toggleCat = (key) => setCollapsedCats((c) => ({ ...c, [key]: !c[key] }));

  const isAllowed = userData.isAdmin || userData.isReferent;

  useEffect(() => {
    dispatch(getFormationsAdmin());
    dispatch(getFormationCategories());
  }, [dispatch]);

  // Message transmis via l'état de navigation (ex. génération partielle) :
  // affiché une fois puis nettoyé pour éviter le re-toast au refresh.
  useEffect(() => {
    const s = location.state;
    if (s && (s.warning || s.success)) {
      if (s.warning) toast.warn(s.warning);
      if (s.success) toast.success(s.success);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const createCat = async (e) => {
    e.preventDefault();
    const value = newCat.trim();
    if (!value) return;
    try {
      await dispatch(createFormationCategory(value));
      setNewCat("");
      toast.success("Catégorie ajoutée");
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de l'ajout");
    }
  };

  const saveEditCat = async (id) => {
    const value = editCatName.trim();
    if (!value) return;
    try {
      await dispatch(editFormationCategory(id, value));
      setEditCatId(null);
      setEditCatName("");
      toast.success("Catégorie modifiée");
      // Rafraîchit la liste : les en-têtes de sections affichent le nom de
      // catégorie porté par les formations, pas par le reducer catégories.
      dispatch(getFormationsAdmin());
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de la modification");
    }
  };

  const deleteCat = (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) return;
    dispatch(deleteFormationCategory(cat.id))
      .then(() => toast.success("Catégorie supprimée"))
      .catch((error) => toast.error(error.response?.data?.message || "Suppression impossible"));
  };

  const togglePublish = async (formation) => {
    try {
      if (formation.status === "published") {
        await dispatch(unpublishFormation(formation.id));
        toast.success("Formation dépubliée");
      } else {
        await dispatch(publishFormation(formation.id));
        toast.success("Formation publiée");
      }
      dispatch(getFormationsAdmin());
    } catch (error) {
      toast.error(error.response?.data?.message || "Action impossible");
    }
  };

  const handleDelete = (formation) => {
    if (!window.confirm(`Supprimer la formation "${formation.title}" ?`)) return;
    dispatch(deleteFormation(formation.id))
      .then(() => toast.success("Formation supprimée"))
      .catch(() => toast.error("Suppression impossible"));
  };

  if (!userData || !userData.id || !isAllowed) return <Login />;

  // Regroupe les formations par catégorie ("Sans catégorie" en dernier)
  const groups = groupFormationsByCategory(list);

  const renderCard = (f) => (
    <div className="admin-formation-card" key={f.id}>
      <div className="afc-main">
        <div className="afc-icon">
          <BookOpen size={22} />
        </div>
        <div className="afc-text">
          <p className="afc-title">{f.title}</p>
          <div className="afc-meta">
            <span className="afc-version">v{f.version}</span>
            <span className={`status-pill ${f.status}`}>
              {f.status === "published" ? "Publiée" : "Brouillon"}
            </span>
          </div>
        </div>
      </div>
      <div className="afc-actions">
        <Link to={`/admin/formations/${f.id}/edition`}>
          <button className="mini-btn">
            <Pencil size={14} /> Éditer
          </button>
        </Link>
        <Link to={`/admin/formations/${f.id}/resultats`}>
          <button className="mini-btn">
            <BarChart3 size={14} /> Résultats
          </button>
        </Link>
        <button className="mini-btn" onClick={() => togglePublish(f)}>
          {f.status === "published" ? "Dépublier" : "Publier"}
        </button>
        <button className="icon-btn danger" onClick={() => handleDelete(f)}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <ToastContainer />
      <Nav />
      <div className="mainContainer">
        <AdminMenu />
        <div className="centerBlock_admin">
          <AdminFormHeader
            title="Gérer les formations"
            subtitle="Créez, organisez et publiez les formations de la Skills Academy."
            actions={
              <>
                <Link to="/admin/formations/statistiques">
                  <button className="mini-btn">
                    <BarChart3 size={14} /> Statistiques
                  </button>
                </Link>
                <button className="mini-btn" onClick={() => setShowCats(true)}>
                  <Tags size={14} /> Catégories
                </button>
                <Link to="/admin/formations/nouvelle">
                  <button className="greenButton">Générer avec l'IA</button>
                </Link>
              </>
            }
          />

          {groups.length > 0 ? (
            <div className="admin-cat-sections">
              {groups.map((group) => {
                const isCollapsed = !!collapsedCats[group.key];
                return (
                  <div
                    className={`admin-cat-section ${isCollapsed ? "collapsed" : ""}`}
                    key={group.key}
                  >
                    <button className="admin-cat-head" onClick={() => toggleCat(group.key)}>
                      <ChevronDown size={16} className="admin-cat-chevron" />
                      <span className="admin-cat-name">{group.name}</span>
                      <span className="admin-cat-count">{group.items.length}</span>
                    </button>
                    {!isCollapsed && (
                      <div className="admin-formations-list">{group.items.map(renderCard)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="results-empty">
              <BookOpen size={40} />
              <p>Aucune formation. Cliquez sur « Générer avec l'IA » pour commencer.</p>
            </div>
          )}
        </div>
      </div>

      {showCats &&
        createPortal(
          <div
            className="palette-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowCats(false);
            }}
          >
            <div className="cat-modal" role="dialog" aria-label="Catégories de formation">
              <div className="cat-modal-head">
                <span className="cat-modal-title">
                  <Tags size={18} /> Catégories de formation
                </span>
                <button className="palette-close" onClick={() => setShowCats(false)} aria-label="Fermer">
                  <X size={16} />
                </button>
              </div>

              <form className="cat-add" onSubmit={createCat}>
                <input
                  type="text"
                  value={newCat}
                  placeholder="Nouvelle catégorie (ex : SEO, E-commerce…)"
                  onChange={(e) => setNewCat(e.target.value)}
                />
                <button type="submit" className="cat-add-btn" disabled={!newCat.trim()}>
                  <Plus size={15} /> Ajouter
                </button>
              </form>

              <div className="cat-list">
                {Array.isArray(categories) && categories.length > 0 ? (
                  categories.map((cat) => (
                    <div className="cat-row" key={cat.id}>
                      {editCatId === cat.id ? (
                        <>
                          <input
                            className="cat-edit-input"
                            type="text"
                            value={editCatName}
                            autoFocus
                            onChange={(e) => setEditCatName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEditCat(cat.id);
                              if (e.key === "Escape") setEditCatId(null);
                            }}
                          />
                          <div className="cat-row-actions">
                            <button className="icon-btn" title="Valider" onClick={() => saveEditCat(cat.id)}>
                              <Check size={15} />
                            </button>
                            <button className="icon-btn" title="Annuler" onClick={() => setEditCatId(null)}>
                              <X size={15} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="cat-name">{cat.name}</span>
                          <div className="cat-row-actions">
                            <button
                              className="icon-btn"
                              title="Renommer"
                              onClick={() => {
                                setEditCatId(cat.id);
                                setEditCatName(cat.name);
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                            <button className="icon-btn danger" title="Supprimer" onClick={() => deleteCat(cat)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="cat-empty">Aucune catégorie pour le moment.</p>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AdminFormations;
